import { useState, useRef, useEffect } from 'react';
import type { Owner, User } from '../lib/types';

/**
 * Filter selection: 'all' = every team, 'connect' = London + Chicago + NYC,
 * 'london'/'chicago'/'nyc' = whole city, 'consult'/'deploy' = solution-desk
 * House books, otherwise a person's OID.
 */
export type TeamFilterValue =
  | 'all'
  | 'connect'
  | 'london'
  | 'chicago'
  | 'nyc'
  | 'consult'
  | 'deploy'
  | string;

interface Props {
  user: User;
  selectedOid: TeamFilterValue;
  onChange: (oid: TeamFilterValue) => void;
  /** OIDs with a current or upcoming book — members outside this set are hidden. */
  activeOids?: ReadonlySet<string>;
}

export function TeamFilter({ user, selectedOid, onChange, activeOids }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (user.role === 'self') return null;

  // Hide members with nothing live or upcoming in the book.
  const hasBook = (m: Owner) => activeOids === undefined || activeOids.has(m.oid);

  const allMembers: Array<{ oid: string; name: string; group: string }> = [];
  if (user.scope.london) {
    for (const m of user.scope.london) if (hasBook(m)) allMembers.push({ ...m, group: 'London' });
  }
  if (user.scope.chicago) {
    for (const m of user.scope.chicago) if (hasBook(m)) allMembers.push({ ...m, group: 'Chicago' });
  }
  if (user.scope.nyc) {
    for (const m of user.scope.nyc) if (hasBook(m)) allMembers.push({ ...m, group: 'New York' });
  }

  const grouped: Record<string, Owner[]> = {};
  for (const m of allMembers) {
    if (!grouped[m.group]) grouped[m.group] = [];
    grouped[m.group].push(m);
  }

  const isLeadership = user.role === 'leadership';
  const allLabel = isLeadership ? 'All Teams' : 'Whole team';

  const groupToCityValue: Record<string, 'london' | 'chicago' | 'nyc'> = {
    London: 'london',
    Chicago: 'chicago',
    'New York': 'nyc',
  };
  const cityLabels: Record<string, string> = {
    london: 'All London',
    chicago: 'All Chicago',
    nyc: 'All NYC',
  };

  const selectedLabel = (() => {
    if (selectedOid === 'all') return allLabel;
    if (selectedOid === 'connect') return 'Connect Only';
    if (selectedOid === 'consult') return 'All Consult';
    if (selectedOid === 'deploy') return 'All Deploy';
    if (selectedOid in cityLabels) return cityLabels[selectedOid];
    return allMembers.find((m) => m.oid === selectedOid)?.name ?? 'Unknown';
  })();

  const solutionOptions: Array<{ value: 'consult' | 'deploy'; label: string }> = [];
  if (isLeadership && (user.scope.consult?.length ?? 0) > 0) {
    solutionOptions.push({ value: 'consult', label: 'All Consult' });
  }
  if (isLeadership && (user.scope.deploy?.length ?? 0) > 0) {
    solutionOptions.push({ value: 'deploy', label: 'All Deploy' });
  }

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ds-btn"
        style={{ minWidth: 170, justifyContent: 'space-between' }}
      >
        <span>{selectedLabel}</span>
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          className={'transition-transform ' + (open ? 'rotate-180' : '')}
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open && (
        <div className="ds-menu-pop">
          <button
            onClick={() => {
              onChange('all');
              setOpen(false);
            }}
            className={
              'ds-menu-item' + (selectedOid === 'all' ? ' active' : '')
            }
          >
            {allLabel}
          </button>
          {isLeadership && (
            <button
              onClick={() => {
                onChange('connect');
                setOpen(false);
              }}
              className={
                'ds-menu-item' + (selectedOid === 'connect' ? ' active' : '')
              }
            >
              Connect Only
            </button>
          )}
          {solutionOptions.length > 0 && (
            <div className="ds-menu-group">
              <div className="ds-menu-head">
                Solutions
              </div>
              {solutionOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={
                    'ds-menu-item' + (selectedOid === opt.value ? ' active' : '')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          {Object.entries(grouped).map(([group, members]) => {
            const cityValue = groupToCityValue[group];
            return (
              <div key={group} className="ds-menu-group">
                <div className="ds-menu-head">
                  {group}
                </div>
                {isLeadership && cityValue && (
                  <button
                    onClick={() => {
                      onChange(cityValue);
                      setOpen(false);
                    }}
                    className={
                      'ds-menu-item' + (selectedOid === cityValue ? ' active' : '')
                    }
                  >
                    {cityLabels[cityValue]}
                  </button>
                )}
                {members.map((m) => (
                  <button
                    key={m.oid}
                    onClick={() => {
                      onChange(m.oid);
                      setOpen(false);
                    }}
                    className={
                      'ds-menu-item' + (selectedOid === m.oid ? ' active' : '')
                    }
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}